/**
 * Forward projection of a learner's path: when they reach the next levels, given how
 * fast they have actually been moving.
 *
 * Every app in the category shows one of these. Loora draws a timeline to "English
 * Confidence", Lingo AI an emoji curve, Verbi a six-month graph beating "other methods"
 * (docs/research/16-category-leaders-2026-10-20.md §4). All three are drawn before the
 * learner has practised once, which means the line is a marketing asset rather than a
 * measurement — it says the same thing to everyone.
 *
 * So this projects from observed pace or it declines to project. That is the same choice
 * the scoring pipeline makes with `ScoreStatus.withheld`: a number we cannot support is
 * worse than an honest absence, because the learner will plan around it. A projection
 * here is always derived from minutes the learner actually spent and sub-levels they
 * actually gained, and the uncertainty widens with distance so that the fourth milestone
 * never reads as a date.
 */

import { BAND_LABELS } from '../cefr/estimate.js';
import { type CefrSublevel, cefrFromIndex, cefrIndex, clamp, DAY_MS, round } from '../types.js';

/** What the learner has actually done, over a window we measured. */
export interface PaceObservation {
  /** Sub-levels gained across the window. May be 0 or negative. */
  readonly sublevelsGained: number;
  /** Practice minutes actually spent in the window — not the goal, the reality. */
  readonly practiceMinutes: number;
  readonly windowDays: number;
}

export interface ProjectionInput {
  readonly current: CefrSublevel;
  /** Confidence of the current estimate, 0–1. Widens the band when low. */
  readonly confidence: number;
  readonly target: CefrSublevel;
  /** The learner's committed daily minutes, which is what converts effort to dates. */
  readonly dailyGoalMinutes: number;
  readonly pace: PaceObservation | null;
  readonly now: number;
}

export type ProjectionWithheldReason =
  /** No window measured yet — the first-open case, where competitors draw a line anyway. */
  | 'no_pace_evidence'
  /** Practised, but not enough of it to divide by. */
  | 'insufficient_practice'
  /** Window too short for the movement in it to mean anything. */
  | 'window_too_short'
  /** Flat or negative movement: a rate of zero projects to never, which is not a date. */
  | 'no_measured_movement'
  /** Nothing to project towards. */
  | 'already_at_target'
  /** A goal of zero minutes a day cannot reach anything. */
  | 'no_daily_goal';

/** Below these the projection is declined rather than guessed. */
export const MIN_PRACTICE_MINUTES = 60;
export const MIN_WINDOW_DAYS = 7;
/**
 * Four sub-levels is roughly a year of steady practice. Past that the arithmetic still
 * works and the claim does not, so milestones beyond it are dropped rather than shown
 * with a wide band — a date two years out is not made honest by an error bar.
 */
export const MAX_PROJECTED_SUBLEVELS = 4;

export interface ProjectedMilestone {
  readonly level: CefrSublevel;
  /** "Intermediate" and so on, for the band the sub-level sits in. */
  readonly label: string;
  readonly etaDays: number;
  readonly etaEpochMs: number;
  /**
   * Half-width of the estimate in days. Grows with distance and with how unsure we are
   * of where the learner is standing now, because both compound.
   */
  readonly uncertaintyDays: number;
}

export interface ProgressProjection {
  readonly status: 'projected' | 'withheld';
  readonly withheldReason: ProjectionWithheldReason | null;
  readonly milestones: readonly ProjectedMilestone[];
  /** Minutes per sub-level, as measured. Shown as the working, not hidden. */
  readonly minutesPerSublevel: number | null;
  /** Echoed back so the learner can see which commitment the dates assume. */
  readonly assumedMinutesPerDay: number;
  /** True when the target was further than {@link MAX_PROJECTED_SUBLEVELS}. */
  readonly truncated: boolean;
}

function withheld(
  reason: ProjectionWithheldReason,
  assumedMinutesPerDay: number,
): ProgressProjection {
  return {
    status: 'withheld',
    withheldReason: reason,
    milestones: [],
    minutesPerSublevel: null,
    assumedMinutesPerDay,
    truncated: false,
  };
}

/**
 * Relative width of the band at the k-th milestone, 0–1 of the ETA itself.
 *
 * Starts at 20% for the next sub-level with a confident estimate, grows 10 points per
 * milestone, and adds up to 30 points for an unsure starting point. Capped at 100%,
 * because a band wider than the estimate says nothing a shrug does not.
 */
export function projectionUncertainty(milestoneIndex: number, confidence: number): number {
  const base = 0.2 + 0.1 * milestoneIndex;
  const fromConfidence = (1 - clamp(confidence, 0, 1)) * 0.3;
  return clamp(base + fromConfidence, 0.2, 1);
}

/**
 * Projects the path to the target, or declines.
 *
 * The rate is `practiceMinutes / sublevelsGained` — the learner's own cost of a
 * sub-level — divided by what they have committed to per day. Nothing is adjusted for
 * difficulty ramp: later sub-levels genuinely take longer, and pretending to model that
 * from one window would be a second invented number on top of the first.
 */
export function projectProgress(input: ProjectionInput): ProgressProjection {
  const assumed = input.dailyGoalMinutes;

  if (!(assumed > 0)) return withheld('no_daily_goal', assumed);

  const currentIndex = cefrIndex(input.current);
  const targetIndex = cefrIndex(input.target);
  const distance = targetIndex - currentIndex;
  if (distance <= 0) return withheld('already_at_target', assumed);

  const pace = input.pace;
  if (pace === null) return withheld('no_pace_evidence', assumed);
  if (pace.windowDays < MIN_WINDOW_DAYS) return withheld('window_too_short', assumed);
  if (pace.practiceMinutes < MIN_PRACTICE_MINUTES) {
    return withheld('insufficient_practice', assumed);
  }
  if (pace.sublevelsGained <= 0) return withheld('no_measured_movement', assumed);

  const minutesPerSublevel = pace.practiceMinutes / pace.sublevelsGained;
  const reachable = Math.min(distance, MAX_PROJECTED_SUBLEVELS);

  const milestones: ProjectedMilestone[] = [];
  for (let step = 1; step <= reachable; step += 1) {
    const etaDays = round((step * minutesPerSublevel) / assumed, 1);
    milestones.push({
      level: cefrFromIndex(currentIndex + step),
      label: BAND_LABELS[cefrFromIndex(currentIndex + step).slice(0, 2)] ?? '',
      etaDays,
      etaEpochMs: input.now + Math.round(etaDays * DAY_MS),
      uncertaintyDays: round(etaDays * projectionUncertainty(step - 1, input.confidence), 1),
    });
  }

  return {
    status: 'projected',
    withheldReason: null,
    milestones,
    minutesPerSublevel: round(minutesPerSublevel, 1),
    assumedMinutesPerDay: assumed,
    truncated: distance > MAX_PROJECTED_SUBLEVELS,
  };
}
